import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trash2, Check, AlertTriangle, Flame, Zap, Wallet,
  ChevronRight, ChevronDown, Package, Search, ExternalLink,
  Loader2, CheckCircle2, XCircle, Copy, Sparkles, Trophy,
  ArrowRight, RefreshCw, Eye, Filter, Globe, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { useApp } from '../contexts/AppContext';
import { DEMO_NFTS, PROJECT_IMAGES } from '../constants';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fire particle animation - using useMemo for random values
const FireParticle = ({ delay, randomY = 50, randomX = 30, randomDuration = 0.25 }) => (
  <motion.div
    className="absolute w-2 h-2 rounded-full bg-gradient-to-t from-orange-500 to-yellow-400"
    initial={{ y: 0, x: 0, opacity: 1, scale: 1 }}
    animate={{ 
      y: -100 - randomY,
      x: (randomX - 0.5) * 60,
      opacity: 0,
      scale: 0
    }}
    transition={{ duration: 1 + randomDuration, delay, ease: "easeOut" }}
  />
);

// Animated flames background
const FlamesBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-orange-500/10 to-transparent" />
    <motion.div 
      className="absolute bottom-0 left-1/4 w-24 h-48 bg-gradient-to-t from-orange-500/30 to-transparent rounded-full blur-xl"
      animate={{ scaleY: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
      transition={{ duration: 2, repeat: Infinity }}
    />
    <motion.div 
      className="absolute bottom-0 right-1/4 w-32 h-40 bg-gradient-to-t from-red-500/20 to-transparent rounded-full blur-xl"
      animate={{ scaleY: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
      transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
    />
  </div>
);

// Group NFTs by policy ID
const groupNFTsByPolicy = (nfts) => {
  return nfts.reduce((acc, nft) => {
    const policyId = nft.policy_id || 'unknown';
    if (!acc[policyId]) {
      acc[policyId] = {
        policy_id: policyId,
        name: nft.project_name || `Project ${policyId.slice(0, 8)}...`,
        image: nft.project_image || null,
        is_rugged: nft.is_rugged || false,
        nfts: []
      };
    }
    acc[policyId].nfts.push(nft);
    return acc;
  }, {});
};

// Group demo NFTs by project name
const groupDemoNFTsByProject = (nfts) => {
  return nfts.reduce((acc, nft) => {
    const project = nft.project || 'Unknown';
    if (!acc[project]) {
      acc[project] = {
        name: project,
        image: PROJECT_IMAGES[project] || PROJECT_IMAGES['Unknown'],
        nfts: [],
        is_rugged: true // Demo NFTs are assumed rugged
      };
    }
    acc[project].nfts.push(nft);
    return acc;
  }, {});
};

// Dump Animation Component
const DumpAnimation = ({ nfts, onComplete }) => {
  const [currentNft, setCurrentNft] = useState(0);
  const [particles, setParticles] = useState([]);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (currentNft < nfts.length) {
      const timer = setTimeout(() => {
        setParticles(prev => [...prev, ...Array(8).fill(0).map((_, i) => ({ id: Date.now() + i }))]);
        setCurrentNft(prev => prev + 1);
      }, 400);
      return () => clearTimeout(timer);
    } else if (currentNft === nfts.length) {
      setTimeout(() => {
        setIsComplete(true);
        setTimeout(onComplete, 1500);
      }, 500);
    }
  }, [currentNft, nfts.length, onComplete]);

  useEffect(() => {
    const cleanup = setInterval(() => {
      setParticles(prev => prev.slice(-20));
    }, 500);
    return () => clearInterval(cleanup);
  }, []);

  return (
    <div className="relative flex flex-col items-center justify-center min-h-[400px] py-8">
      <FlamesBackground />
      
      <div className="w-full max-w-md mb-8 relative z-10">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-white/70">Burning NFTs...</span>
          <span className="text-orange-400 font-bold">{currentNft} / {nfts.length}</span>
        </div>
        <Progress value={(currentNft / nfts.length) * 100} className="h-3 bg-white/10" />
      </div>

      <div className="relative">
        <AnimatePresence mode="wait">
          {currentNft < nfts.length && (
            <motion.div
              key={currentNft}
              initial={{ scale: 1, y: 0, opacity: 1, rotate: 0 }}
              animate={{ scale: 0, y: 100, opacity: 0, rotate: 180 }}
              transition={{ duration: 0.4 }}
              className="relative"
            >
              <div className="w-32 h-32 rounded-xl overflow-hidden border-2 border-orange-500 shadow-lg shadow-orange-500/50">
                <img 
                  src={nfts[currentNft]?.image || 'https://via.placeholder.com/128?text=NFT'} 
                  alt={nfts[currentNft]?.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/128?text=NFT'; }}
                />
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                {particles.slice(-8).map((p, i) => (
                  <FireParticle key={p.id} delay={i * 0.05} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div 
          className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-40 h-20"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 0.5, repeat: Infinity }}
        >
          <div className="w-full h-full bg-gradient-to-t from-orange-600 via-orange-500 to-yellow-400 rounded-full blur-md opacity-80" />
        </motion.div>
      </div>

      <AnimatePresence>
        {isComplete && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm rounded-xl"
          >
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ duration: 0.5 }}
                className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mx-auto mb-4"
              >
                <Flame className="w-12 h-12 text-white" />
              </motion.div>
              <h3 className="text-2xl font-bold text-white mb-2">NFTs Burned! 🔥</h3>
              <p className="text-orange-400">{nfts.length} NFTs sent to the dump wallet</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Transaction Status Component
const TransactionStatus = ({ status, txHash, xpAwarded }) => {
  const statusConfig = {
    pending: { icon: Loader2, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Confirming...' },
    confirmed: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Confirmed!' },
    failed: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Failed' }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Card className="bg-surface-2 border-white/10">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-full ${config.bg} flex items-center justify-center`}>
            <Icon className={`w-8 h-8 ${config.color} ${status === 'pending' ? 'animate-spin' : ''}`} />
          </div>
          <div className="flex-1">
            <h3 className={`text-xl font-bold ${config.color}`}>{config.label}</h3>
            {txHash && (
              <a 
                href={`https://cardanoscan.io/transaction/${txHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-text-secondary hover:text-white flex items-center gap-1 mt-1"
              >
                View on Cardanoscan <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          {status === 'confirmed' && xpAwarded > 0 && (
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              className="text-center"
            >
              <div className="text-3xl font-bold text-primary-theme">+{xpAwarded}</div>
              <div className="text-sm text-text-secondary">XP Earned</div>
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Loading skeleton for NFTs
const NFTLoadingSkeleton = () => (
  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
    {[...Array(12)].map((_, i) => (
      <div key={i} className="aspect-square rounded-xl bg-white/5 animate-pulse" />
    ))}
  </div>
);

export const DumpPage = () => {
  const { user, setWalletModalOpen } = useApp();
  const [step, setStep] = useState(1);
  const [selectedNfts, setSelectedNfts] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [dumpConfig, setDumpConfig] = useState(null);
  const [txHash, setTxHash] = useState('');
  const [txStatus, setTxStatus] = useState('pending');
  const [xpAwarded, setXpAwarded] = useState(0);
  const [copied, setCopied] = useState(false);
  
  // Live wallet scanning state
  const [walletNfts, setWalletNfts] = useState([]);
  const [loadingNfts, setLoadingNfts] = useState(false);
  const [nftError, setNftError] = useState(null);
  const [filterRugged, setFilterRugged] = useState(true);
  const [nftStats, setNftStats] = useState({ total: 0, rugged: 0, filtered: false });
  const [useDemoData, setUseDemoData] = useState(false);

  // Determine which NFTs to show
  const displayNfts = useDemoData ? DEMO_NFTS : walletNfts;
  const groupedNfts = useDemoData 
    ? groupDemoNFTsByProject(displayNfts)
    : groupNFTsByPolicy(displayNfts);
  
  const filteredGroups = Object.entries(groupedNfts).filter(([name, group]) => {
    const nameMatch = (group.name || name).toLowerCase().includes(searchQuery.toLowerCase());
    return nameMatch;
  });

  // Fetch dump config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/dump/config`);
        if (res.ok) {
          const data = await res.json();
          setDumpConfig(data);
        }
      } catch (e) {
        setDumpConfig({
          dump_wallet: 'addr1qxel0rg2az66uv0ktkwwggwavdwhm68x5zed6g8g4f74v5nrpqj87qghnz9ln78lj9c7mqqh8gufj5zm0s05r78tvlys6v6r9z',
          min_ada_amount: 2,
          xp_per_nft: 100
        });
      }
    };
    fetchConfig();
  }, []);

  // Fetch wallet NFTs when user connects
  const fetchWalletNfts = useCallback(async (walletAddress) => {
    if (!walletAddress || walletAddress.includes('demo')) {
      setUseDemoData(true);
      return;
    }

    setLoadingNfts(true);
    setNftError(null);
    setUseDemoData(false);

    try {
      const res = await fetch(`${API_URL}/api/wallet/${walletAddress}/nfts?filter_rugged=${filterRugged}`);
      if (res.ok) {
        const data = await res.json();
        setWalletNfts(data.nfts || []);
        setNftStats({
          total: data.all_nfts_count || 0,
          rugged: data.rugged_nfts_count || 0,
          filtered: data.filter_active || false
        });
        
        // If no NFTs found, show demo data
        if ((data.nfts || []).length === 0) {
          setUseDemoData(true);
          setNftError('No NFTs found in wallet. Showing demo NFTs.');
        }
      } else {
        throw new Error('Failed to fetch NFTs');
      }
    } catch (e) {
      setNftError('Could not load wallet NFTs. Showing demo data.');
      setUseDemoData(true);
    } finally {
      setLoadingNfts(false);
    }
  }, [filterRugged]);

  // Trigger NFT fetch when user changes
  useEffect(() => {
    if (user?.wallet_address) {
      fetchWalletNfts(user.wallet_address);
    } else if (user) {
      setUseDemoData(true);
    }
  }, [user, fetchWalletNfts]);

  // Refresh NFTs with filter toggle
  const handleFilterToggle = () => {
    const newFilter = !filterRugged;
    setFilterRugged(newFilter);
    if (user?.wallet_address && !user.wallet_address.includes('demo')) {
      setLoadingNfts(true);
      fetch(`${API_URL}/api/wallet/${user.wallet_address}/nfts?filter_rugged=${newFilter}`)
        .then(res => res.json())
        .then(data => {
          setWalletNfts(data.nfts || []);
          setNftStats({
            total: data.all_nfts_count || 0,
            rugged: data.rugged_nfts_count || 0,
            filtered: data.filter_active || false
          });
        })
        .catch(() => setUseDemoData(true))
        .finally(() => setLoadingNfts(false));
    }
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const selectAllFromProject = (projectNfts) => {
    const projectIds = projectNfts.map(n => n.id || n.unit);
    const allSelected = projectIds.every(id => selectedNfts.includes(id));
    
    if (allSelected) {
      setSelectedNfts(prev => prev.filter(id => !projectIds.includes(id)));
    } else {
      setSelectedNfts(prev => [...new Set([...prev, ...projectIds])]);
    }
  };

  const toggleNft = (nftId) => {
    setSelectedNfts(prev => 
      prev.includes(nftId) 
        ? prev.filter(id => id !== nftId)
        : [...prev, nftId]
    );
  };

  const copyWallet = () => {
    navigator.clipboard.writeText(dumpConfig?.dump_wallet || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const selectedNftDetails = displayNfts.filter(nft => selectedNfts.includes(nft.id || nft.unit));
  const totalXp = selectedNfts.length * (dumpConfig?.xp_per_nft || 100);

  const handleProceedToReview = () => {
    if (selectedNfts.length === 0) return;
    setStep(2);
  };

  const handleStartDump = () => {
    setStep(3);
  };

  const handleDumpAnimationComplete = useCallback(() => {
    setStep(4);
    setTxStatus('pending');
    setTimeout(() => {
      setTxStatus('confirmed');
      setXpAwarded(totalXp);
      setTxHash('demo_tx_' + Date.now());
      setTimeout(() => setStep(5), 1500);
    }, 3000);
  }, [totalXp]);

  const handleReset = () => {
    setStep(1);
    setSelectedNfts([]);
    setTxHash('');
    setTxStatus('pending');
    setXpAwarded(0);
  };

  // Not connected state
  if (!user) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/30">
          <CardContent className="p-12 text-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mx-auto mb-6">
              <Flame className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Connect to Dump NFTs</h2>
            <p className="text-text-secondary mb-8 max-w-md mx-auto">
              Connect your Cardano wallet to dump your rugged NFTs and earn XP rewards!
            </p>
            <Button 
              onClick={() => setWalletModalOpen(true)}
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:opacity-90 text-white px-8 h-12"
            >
              <Wallet className="w-5 h-5 mr-2" />Connect Wallet
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 5: Complete
  if (step === 5) {
    return (
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-green-400 rounded-full"
                  initial={{ x: Math.random() * 100 + '%', y: '100%', opacity: 0 }}
                  animate={{ y: '-100%', opacity: [0, 1, 0] }}
                  transition={{ duration: 2 + Math.random(), repeat: Infinity, delay: Math.random() * 2 }}
                />
              ))}
            </div>
            <CardContent className="p-12 text-center relative">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ duration: 0.6 }}
                className="w-32 h-32 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/50"
              >
                <Trophy className="w-16 h-16 text-white" />
              </motion.div>
              
              <h2 className="text-4xl font-bold text-white mb-2">Dump Complete! 🎉</h2>
              <p className="text-text-secondary mb-8">Your NFTs have been burned and XP awarded</p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-surface-2 rounded-xl p-6">
                  <div className="text-4xl font-bold text-primary-theme">{selectedNfts.length}</div>
                  <div className="text-text-secondary text-sm">NFTs Dumped</div>
                </div>
                <div className="bg-surface-2 rounded-xl p-6">
                  <motion.div 
                    className="text-4xl font-bold text-green-400"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring" }}
                  >
                    +{xpAwarded}
                  </motion.div>
                  <div className="text-text-secondary text-sm">XP Earned</div>
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <Button onClick={handleReset} className="bg-primary-theme hover:opacity-90 text-white">
                  <RefreshCw className="w-4 h-4 mr-2" />Dump More
                </Button>
                <Button variant="outline" className="border-white/20 text-white">
                  <Eye className="w-4 h-4 mr-2" />View Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Step 4: Transaction Status
  if (step === 4) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold text-white text-center mb-8">Verifying Transaction...</h1>
        <TransactionStatus status={txStatus} txHash={txHash} xpAwarded={xpAwarded} />
      </div>
    );
  }

  // Step 3: Dump Animation
  if (step === 3) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="bg-surface-2 border-white/10 overflow-hidden">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white">🔥 Burning NFTs...</CardTitle>
            <CardDescription className="text-text-secondary">Watch your rugged NFTs become XP!</CardDescription>
          </CardHeader>
          <CardContent>
            <DumpAnimation nfts={selectedNftDetails} onComplete={handleDumpAnimationComplete} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Review
  if (step === 2) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setStep(1)} className="text-white/70 hover:text-white">← Back</Button>
          <h1 className="text-2xl font-bold text-white">Review Your Dump</h1>
        </div>

        <Card className="bg-surface-2 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-primary-theme" />
              {selectedNfts.length} NFTs Selected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 max-h-48 overflow-y-auto">
              {selectedNftDetails.map(nft => (
                <div key={nft.id || nft.unit} className="relative group">
                  <img 
                    src={nft.image || 'https://via.placeholder.com/64?text=NFT'} 
                    alt={nft.name}
                    className="w-full aspect-square rounded-lg object-cover border border-white/10"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=NFT'; }}
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <span className="text-xs text-white text-center px-1">{nft.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-surface-2 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-secondary-theme" />Dump Wallet
            </CardTitle>
            <CardDescription className="text-text-secondary">Send your NFTs to this address</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 bg-black/30 rounded-lg p-3">
              <code className="text-sm text-green-400 flex-1 break-all">{dumpConfig?.dump_wallet || 'Loading...'}</code>
              <Button size="sm" variant="ghost" onClick={copyWallet} className="text-white/70 hover:text-white">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-yellow-400 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Include minimum {dumpConfig?.min_ada_amount || 2} ADA for transaction fees
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold">You will earn</h3>
                <p className="text-text-secondary text-sm">{selectedNfts.length} NFTs × {dumpConfig?.xp_per_nft || 100} XP each</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-primary-theme">+{totalXp}</div>
                <div className="text-text-secondary text-sm">XP</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button 
          className="w-full h-14 bg-gradient-to-r from-orange-500 to-red-600 hover:opacity-90 text-white text-lg font-bold"
          onClick={handleStartDump}
        >
          <Flame className="w-5 h-5 mr-2" />Burn {selectedNfts.length} NFTs
        </Button>
      </div>
    );
  }

  // Step 1: Select NFTs
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            Dump Zone
          </h1>
          <p className="text-text-secondary mt-1">
            {useDemoData ? 'Demo Mode - Connect a real wallet to see your NFTs' : 'Select NFTs from your wallet to burn and earn XP'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Badge className="bg-primary-theme/20 text-primary-theme border-0 px-4 py-2">
            <Zap className="w-4 h-4 mr-1" />
            {dumpConfig?.xp_per_nft || 100} XP per NFT
          </Badge>
        </div>
      </div>

      {/* Wallet Stats & Filter Toggle */}
      {!useDemoData && (
        <Card className="bg-surface-2 border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-text-secondary text-sm">Total NFTs</p>
                  <p className="text-2xl font-bold text-white">{nftStats.total}</p>
                </div>
                <div>
                  <p className="text-text-secondary text-sm">Rugged NFTs</p>
                  <p className="text-2xl font-bold text-orange-400">{nftStats.rugged}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-text-secondary text-sm">Show only rugged</span>
                <Switch checked={filterRugged} onCheckedChange={handleFilterToggle} />
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => fetchWalletNfts(user?.wallet_address)}
                  className="border-white/20 text-white"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingNfts ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Demo Mode Notice */}
      {useDemoData && (
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <div>
              <p className="text-blue-400 font-medium">Demo Mode Active</p>
              <p className="text-text-secondary text-sm">
                {nftError || 'Connect a real Cardano wallet to see your actual NFTs'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
        <Input 
          placeholder="Search projects..." 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-surface-2 border-white/10 text-white h-12"
        />
      </div>

      {/* Selection Summary */}
      {selectedNfts.length > 0 && (
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/20">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  {selectedNftDetails.slice(0, 4).map(nft => (
                    <img 
                      key={nft.id || nft.unit}
                      src={nft.image || 'https://via.placeholder.com/40?text=NFT'} 
                      alt={nft.name}
                      className="w-10 h-10 rounded-lg border-2 border-surface-2"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=NFT'; }}
                    />
                  ))}
                  {selectedNfts.length > 4 && (
                    <div className="w-10 h-10 rounded-lg border-2 border-surface-2 bg-surface-3 flex items-center justify-center text-sm text-white">
                      +{selectedNfts.length - 4}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-white font-semibold">{selectedNfts.length} NFTs Selected</p>
                  <p className="text-primary-theme text-sm">+{totalXp} XP potential</p>
                </div>
              </div>
              <Button className="bg-primary-theme hover:opacity-90 text-white" onClick={handleProceedToReview}>
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Loading State */}
      {loadingNfts && (
        <Card className="bg-surface-2 border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <Loader2 className="w-6 h-6 text-primary-theme animate-spin" />
              <span className="text-white">Scanning your wallet for NFTs...</span>
            </div>
            <NFTLoadingSkeleton />
          </CardContent>
        </Card>
      )}

      {/* NFT Projects */}
      {!loadingNfts && (
        <div className="space-y-4">
          {filteredGroups.map(([projectId, project]) => {
            const isExpanded = expandedProjects[projectId];
            const projectNfts = project.nfts;
            const selectedFromProject = projectNfts.filter(n => selectedNfts.includes(n.id || n.unit)).length;
            const allSelected = selectedFromProject === projectNfts.length;

            return (
              <Card key={projectId} className="bg-surface-2 border-white/10 overflow-hidden">
                <CardHeader 
                  className="cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => toggleProject(projectId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {project.image ? (
                        <img src={project.image} alt={project.name} className="w-14 h-14 rounded-xl object-cover" 
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
                          <Package className="w-6 h-6 text-orange-400" />
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-white text-lg flex items-center gap-2">
                          {project.name}
                          {project.is_rugged && (
                            <Badge className="bg-orange-500/20 text-orange-400 border-0 text-xs">Rugged</Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="text-text-secondary">
                          {projectNfts.length} NFTs available
                          {selectedFromProject > 0 && (
                            <span className="text-primary-theme ml-2">• {selectedFromProject} selected</span>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        size="sm"
                        variant={allSelected ? "default" : "outline"}
                        className={allSelected ? "bg-primary-theme text-white" : "border-white/20 text-white hover:bg-white/10"}
                        onClick={(e) => { e.stopPropagation(); selectAllFromProject(projectNfts); }}
                      >
                        {allSelected ? <Check className="w-4 h-4 mr-1" /> : null}
                        {allSelected ? 'Selected' : 'Select All'}
                      </Button>
                      {isExpanded ? <ChevronDown className="w-5 h-5 text-text-muted" /> : <ChevronRight className="w-5 h-5 text-text-muted" />}
                    </div>
                  </div>
                </CardHeader>
                
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CardContent className="pt-0">
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                          {projectNfts.map(nft => {
                            const nftId = nft.id || nft.unit;
                            const isSelected = selectedNfts.includes(nftId);
                            return (
                              <motion.div
                                key={nftId}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                                  isSelected ? 'border-primary-theme shadow-lg shadow-orange-500/30' : 'border-transparent hover:border-white/30'
                                }`}
                                onClick={() => toggleNft(nftId)}
                              >
                                <img 
                                  src={nft.image || 'https://via.placeholder.com/100?text=NFT'} 
                                  alt={nft.name}
                                  className="w-full aspect-square object-cover"
                                  onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=NFT'; }}
                                />
                                <div className={`absolute inset-0 bg-black/40 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                                {isSelected && (
                                  <motion.div 
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute top-1 right-1 w-6 h-6 bg-primary-theme rounded-full flex items-center justify-center"
                                  >
                                    <Check className="w-4 h-4 text-white" />
                                  </motion.div>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/80 to-transparent">
                                  <p className="text-[10px] text-white truncate">{nft.name}</p>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            );
          })}
        </div>
      )}

      {!loadingNfts && filteredGroups.length === 0 && (
        <Card className="bg-surface-2 border-white/10">
          <CardContent className="py-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-text-muted" />
            <p className="text-text-secondary">
              {searchQuery ? `No NFTs found matching "${searchQuery}"` : 'No NFTs found in your wallet'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DumpPage;
